import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./cashier.css";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Select from "../ui/Select";
import * as api from "../../utils/api/deliveryDocumentation";
import {
  EVIDENCE_TYPES,
  nextRequiredEvidence,
} from "../../utils/cashier/deliveryDocumentation";
import { useCashierText } from "./cashierText";
import AsyncState from "./AsyncState";
import CashierSummary from "./CashierSummary";
import CashierTabs from "./CashierTabs";
import CreateOrderSheet from "./CreateOrderSheet";
import CameraCapture from "./CameraCapture";
import OrderCard from "./OrderCard";
import OutcomeSheet from "./OutcomeSheet";
import { DeleteModal } from "../modals/Delete";
import { InputModal } from "../modals/InputModal";

const SAUDI_TIME_ZONE = "Asia/Riyadh";
const BUSINESS_DAY_START_HOUR = 5;
const SAUDI_UTC_OFFSET_HOURS = 3;

function saudiDateParts(date) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SAUDI_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  return Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  );
}

function msUntilNextBusinessDay(date = new Date()) {
  const parts = saudiDateParts(date);
  const todayStart = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    BUSINESS_DAY_START_HOUR - SAUDI_UTC_OFFSET_HOURS,
  );
  const nextStart =
    parts.hour < BUSINESS_DAY_START_HOUR
      ? todayStart
      : todayStart + 24 * 60 * 60 * 1000;
  return Math.max(0, nextStart - date.getTime());
}

export default function CashierDocumentation() {
  const { t, dir, evidence, errorMessage } = useCashierText();
  const [context, setContext] = useState({ stores: [] });
  const [contextLoaded, setContextLoaded] = useState(false);
  const [restricted, setRestricted] = useState(false);
  // Only the newest list request may write the list: switching store / branch / tab never shows
  // another selection's documents.
  const requestSeq = useRef(0);
  const [summary, setSummary] = useState({});
  const [storeId, setStoreId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [camera, setCamera] = useState(null);
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [businessDayTick, setBusinessDayTick] = useState(0);
  const [reasonRequest, setReasonRequest] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const store = useMemo(
    () => context.stores.find((x) => x.id === storeId),
    [context, storeId],
  );
  const branch = useMemo(
    () => store?.branches.find((x) => x.id === branchId),
    [store, branchId],
  );
  const hasValidContext = Boolean(store && branch);
  const storeOptions = context.stores.map((x) => ({
    label: x.name,
    value: x.id,
  }));
  const branchOptions =
    store?.branches.map((x) => ({
      label: x.name,
      value: x.id,
    })) || [];
  const refreshSummary = useCallback(
    () =>
      hasValidContext
        ? api
            .getCashierSummary({ storeId, branchId })
            .then((x) => setSummary(x.summary))
            .catch(() => setSummary({}))
        : Promise.resolve(setSummary({})),
    [hasValidContext, storeId, branchId],
  );
  const load = useCallback(
    async (page = 1) => {
      const seq = ++requestSeq.current;
      if (!hasValidContext) {
        setOrders([]);
        setPagination({ page: 1, totalPages: 1 });
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const data = await api.getDocumentationOrders({
          storeId,
          branchId,
          view: tab,
          searchLast4: search,
          page,
          limit: 20,
        });
        if (seq !== requestSeq.current) return;
        setOrders(data.items);
        setPagination(data.pagination);
      } catch (e) {
        if (seq === requestSeq.current) setError(errorMessage(e));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- errorMessage only follows the language
    [hasValidContext, tab, search, storeId, branchId],
  );
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await api.getDeliveryDocumentationContext();
        setContext({ ...data, stores: data.stores || [] });
        const firstStore = data.stores?.[0];
        const firstBranch = firstStore?.branches?.[0];
        setStoreId(firstStore?.id || "");
        setBranchId(firstBranch?.id || "");
      } catch (e) {
        if (e.code === "BRANCH_ASSIGNMENT_REQUIRED" || e.status === 403)
          setRestricted(true);
        else setError(errorMessage(e));
      } finally {
        setContextLoaded(true);
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loaded once; errorMessage only follows the language
  }, []);
  // A new store / branch starts from an empty list and summary, never the previous selection's data.
  useEffect(() => {
    setOrders([]);
    setSummary({});
    setPagination({ page: 1, totalPages: 1 });
    setDetails(null);
    setCamera(null);
  }, [storeId, branchId]);
  useEffect(() => {
    const stores = context.stores || [];
    if (!stores.length) {
      if (storeId) setStoreId("");
      if (branchId) setBranchId("");
      return;
    }
    const selectedStore = stores.find((x) => x.id === storeId) || stores[0];
    if (selectedStore.id !== storeId) {
      setStoreId(selectedStore.id);
      setBranchId(selectedStore.branches?.[0]?.id || "");
      return;
    }
    const selectedBranch = selectedStore.branches?.find(
      (x) => x.id === branchId,
    );
    if (!selectedBranch) setBranchId(selectedStore.branches?.[0]?.id || "");
  }, [context, storeId, branchId]);
  useEffect(() => {
    if (hasValidContext) {
      refreshSummary();
      load(1);
    }
  }, [hasValidContext, refreshSummary, load]);
  useEffect(() => {
    if (!hasValidContext) return undefined;
    const timer = window.setTimeout(() => {
      api
        .getDeliveryDocumentationContext()
        .then((data) => setContext({ ...data, stores: data.stores || [] }))
        .catch(() => {});
      refreshSummary();
      load(1);
      setBusinessDayTick((value) => value + 1);
    }, msUntilNextBusinessDay() + 1000);
    return () => window.clearTimeout(timer);
  }, [hasValidContext, refreshSummary, load, businessDayTick]);
  const mutate = async (work, message) => {
    setBusy(true);
    setNotice("");
    try {
      const result = await work();
      setNotice(message);
      await Promise.all([load(pagination.page), refreshSummary()]);
      return result;
    } catch (e) {
      setNotice(errorMessage(e));
      throw e;
    } finally {
      setBusy(false);
    }
  };
  const create = async (payload) => {
    try {
      if (!hasValidContext) {
        setNotice(t.noBranchForOrder);
        return;
      }
      const data = await mutate(
        () =>
          api.createDocumentationOrder({
            ...payload,
            storeId,
            branchId,
          }),
        t.orderCreated,
      );
      setCreateOpen(false);
      if (data.similarOrderWarning?.exists)
        setNotice(t.similarOrder);
      const first = nextRequiredEvidence(data.order);
      setCamera(
        first ? { order: data.order, type: first, replace: false } : null,
      );
    } catch {}
  };
  const capture = async (file) => {
    const current = camera;
    if (!current) throw new Error(t.noPendingPhoto);
    setBusy(true);
    setNotice("");
    let data;
    try {
      data = await api.uploadEvidence(
        current.order._id,
        current.type.key,
        file,
        current.replace,
      );
    } catch (error) {
      const message = errorMessage(error);
      setNotice(message);
      throw Object.assign(error, { message });
    } finally {
      setBusy(false);
    }
    setOrders((items) =>
      items.map((item) => (item._id === data.order._id ? data.order : item)),
    );
    setNotice(current.replace ? t.photoReplaced : t.photoUploaded);
    const next = current.replace ? null : nextRequiredEvidence(data.order);
    setCamera(next ? { order: data.order, type: next, replace: false } : null);
    // These are derived views; they must never decide whether the upload itself
    // succeeded or keep the confirmation UI spinning.
    Promise.allSettled([load(pagination.page), refreshSummary()]);
    return data;
  };
  const requestDeleteReason = (label, onReason) => {
    setReasonRequest({
      label: label || t.writeReason,
      onConfirm: (reason) => {
        setReasonRequest(null);
        if (!reason) return;
        setDeleteRequest({
          description: label,
          handleDelete: async () => {
            try {
              await onReason(reason);
            } finally {
              setDeleteRequest(null);
            }
          },
        });
      },
    });
  };
  const requestDeleteConfirmation = (description, onConfirm) => {
    setDeleteRequest({
      description,
      handleDelete: async () => {
        try {
          await onConfirm();
        } finally {
          setDeleteRequest(null);
        }
      },
    });
  };
  const replaceOrder = (nextOrder) => {
    setOrders((items) =>
      items.map((item) => (item._id === nextOrder._id ? nextOrder : item)),
    );
    setDetails(nextOrder);
  };
  // No assigned branch (or no store with one): a clear restricted state, never unrestricted data.
  if (restricted || (contextLoaded && !error && !context.stores.length)) {
    return (
      <main className="cashier-page" dir={dir}>
        <section className="cashier-hero">
          <div className="cashier-hero-row">
            <div>
              <h1>{t.title}</h1>
              <p>{t.subtitle}</p>
            </div>
          </div>
        </section>
        <div className="cashier-empty" role="status">
          <b>{t.restrictedTitle}</b>
          <p>{t.restrictedBody}</p>
        </div>
      </main>
    );
  }
  return (
    <main className="cashier-page" dir={dir}>
      <section className="cashier-hero">
        <div className="cashier-hero-row">
          <div>
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
          </div>
          <div>
            <Button
            variant="primary"
            appearance="filled"
              disabled={!hasValidContext}
              onClick={() => setCreateOpen(true)}
            >
              {t.newOrder}
            </Button>
          </div>
        </div>
        <div className="cashier-context">
          <div>
            <b>{t.currentBranch}</b>
            <small>{context.businessDate}</small>
          </div>
          <div>
            {!context.stores.length && <strong>{t.noBranches}</strong>}
            {context.stores.length === 1 && store && (
              <strong>{store.name}</strong>
            )}
            {context.stores.length > 1 && (
              <Select
                aria-label={t.store}
                value={storeId}
                onChange={(e) => {
                  const id = e.target.value;
                  const next = context.stores.find((x) => x.id === id);
                  setStoreId(id);
                  setBranchId(next?.branches[0]?.id || "");
                }}
                options={storeOptions}
              />
            )}
            {store && store.branches.length === 0 && (
              <strong>{t.noStoreBranches}</strong>
            )}
            {store?.branches.length === 1 && branch && (
              <strong>{branch.name}</strong>
            )}
            {store?.branches.length > 1 && (
              <Select
                aria-label={t.branch}
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                options={branchOptions}
              />
            )}
          </div>
        </div>
      </section>
      <CashierSummary summary={summary} />
      <CashierTabs active={tab} summary={summary} onChange={setTab} />
      {notice && <div className="cashier-notice">{notice}</div>}
      <form
          className="cashier-search"
          onSubmit={(e) => {
            e.preventDefault();
            load(1);
          }}
        >
          <Input
            value={search}
            fullWidth={true}
            onChange={(e) =>
              setSearch(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
            inputMode="numeric"
            placeholder={t.searchPlaceholder}
          />
          <Button variant="primary" appearance="filled" type="submit">
            {t.search}
          </Button>
        </form>
        <AsyncState
          loading={loading}
          error={error}
          empty={!orders.length}
          onRetry={() => load()}
        >
          <div>
            {orders.map((order) => (
              <OrderCard
                key={order._id}
                order={order}
                onEvidence={(item, type, replace) =>
                  setCamera({ order: item, type, replace })
                }
                onDeleteEvidence={(item, type) => {
                  requestDeleteConfirmation(
                    t.deletePhotoConfirm,
                    () =>
                      mutate(
                        () =>
                          api.deleteEvidence(
                            item._id,
                            type.key,
                            t.deletePhotoReason,
                          ),
                        t.photoDeleted,
                        ),
                  );
                }}
                onDetails={setDetails}
                onRevertConclusion={async (conclusionId) => {
                  const result = await mutate(
                    () =>
                      api.revertDocumentationConclusion(
                        details._id,
                        conclusionId,
                      ),
                    t.conclusionReverted,
                  );
                  replaceOrder(result.order);
                }}
                onDelete={(item) => {
                  requestDeleteReason(t.deleteOrderReason, (reason) =>
                    mutate(
                      () => api.deleteDocumentationOrder(item._id, reason),
                      t.orderDeleted,
                    ),
                  );
                }}
              />
            ))}
          </div>
        </AsyncState>
      <div className="cashier-pagination">
        <Button
          variant="neutral"
          disabled={!pagination.hasPreviousPage}
          onClick={() => load(pagination.page - 1)}
        >
          {t.previous}
        </Button>
        <span>
          {pagination.page || 1} / {pagination.totalPages || 1}
        </span>
        <Button
          variant="neutral"
          disabled={!pagination.hasNextPage}
          onClick={() => load(pagination.page + 1)}
        >
          {t.next}
        </Button>
      </div>
      <CreateOrderSheet
        open={createOpen}
        context={context}
        selectedStore={storeId}
        selectedBranch={branchId}
        onClose={() => setCreateOpen(false)}
        onSubmit={create}
        busy={busy}
      />
      <CameraCapture
        open={Boolean(camera)}
        evidence={camera?.type && evidence(camera.type)}
        progress={
          camera && {
            current:
              camera.order.requiredEvidenceTypes
                ?.filter((key) =>
                  EVIDENCE_TYPES.some((type) => type.key === key),
                )
                .indexOf(camera.type.key) + 1,
            total: camera.order.requiredEvidenceTypes?.length || 1,
          }
        }
        onClose={() => setCamera(null)}
        onConfirm={capture}
      />
      {details && (
        <OutcomeSheet
          order={details}
          busy={busy}
          onClose={() => setDetails(null)}
          onSaveOutcome={async ({ conclusion, note }) => {
            let currentOrder = details;
            if (conclusion) {
              const result = await mutate(
                () =>
                  api.addDocumentationConclusion(currentOrder._id, {
                    ...conclusion,
                    note,
                  }),
                note ? t.conclusionAndNote : t.conclusionAdded,
              );
              currentOrder = result.order;
              replaceOrder(currentOrder);
            }
            if (note && !conclusion) {
              const result = await mutate(
                () =>
                  api.addDocumentationNote(currentOrder._id, {
                    note,
                  }),
                t.noteAdded,
              );
              replaceOrder(result.order);
            }
          }}
        />
      )}
      {reasonRequest && (
        <InputModal
          label={reasonRequest.label}
          placeholder={t.writeReason}
          onConfirm={reasonRequest.onConfirm}
          onCancel={() => setReasonRequest(null)}
        />
      )}
      {deleteRequest && (
        <DeleteModal
          description={deleteRequest.description}
          loading={busy}
          handleDelete={deleteRequest.handleDelete}
          cancel={() => setDeleteRequest(null)}
        />
      )}
    </main>
  );
}
